import { useEffect, useRef, useState } from 'react';
import { encodeSlideImage, type EncodedSlideImage } from '../lib/encodeSlideImage.ts';
import { errorMessage } from '../lib/api.ts';
import type { UploadedSlide } from '../editor/useDeckEditor.ts';

/** The encode result plus the object URL the 16:9 preview renders. */
type Prepared = EncodedSlideImage & { previewUrl: string };

/**
 * "Upload a finished slide" pane inside the add-slide chooser — for slides
 * designed outside the system (Photoshop/InDesign) and exported as an image.
 * Runs the client-side re-encode (lib/encodeSlideImage.js), previews the
 * artwork in a 16:9 frame with the chrome positions ghosted, and hands a
 * ready FormData payload up via onUpload. Expert path: the inserted slide is
 * a flat image — not editable or re-themable in the studio.
 */
export default function UploadSlidePane({
  onUpload,
  busy,
}: {
  onUpload: (slide: UploadedSlide) => void;
  busy?: boolean;
}) {
  const fileRef = useRef<HTMLInputElement>(null);
  const [prepared, setPrepared] = useState<Prepared | null>(null);
  const [title, setTitle] = useState('');
  const [chrome, setChrome] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(
    () => () => {
      if (prepared?.previewUrl) URL.revokeObjectURL(prepared.previewUrl);
    },
    [prepared],
  );

  async function handleFile(file: File | null | undefined) {
    if (!file) return;
    setError(null);
    setProcessing(true);
    try {
      const result = await encodeSlideImage(file);
      setPrepared((prev) => {
        if (prev?.previewUrl) URL.revokeObjectURL(prev.previewUrl);
        return { ...result, previewUrl: URL.createObjectURL(result.blob) };
      });
      setTitle(file.name.replace(/\.[^.]+$/, '').replace(/[-_]+/g, ' '));
    } catch (err) {
      setPrepared(null);
      setError(errorMessage(err));
    } finally {
      setProcessing(false);
    }
  }

  function insert() {
    if (!prepared || busy) return;
    onUpload({
      blob: prepared.blob,
      filename: prepared.filename,
      title: title.trim(),
      chrome,
      fit: prepared.aspectOk ? 'cover' : 'contain',
    });
  }

  const ghost = chrome && prepared;

  return (
    <div
      className="upload-slide"
      style={{ padding: '1.2rem 1.4rem', overflow: 'auto', display: 'flex', flexDirection: 'column', gap: '0.9rem' }}
    >
      <p className="muted small" style={{ margin: 0, maxWidth: '60ch' }}>
        For slides designed in Photoshop or InDesign. Export at 3840×2160 (16:9). Leave the artwork free of your own
        logo, footer and page number; the RKH chrome is added on top.{' '}
        <strong>This slide won't be editable or re-themable in the studio.</strong>
      </p>

      <div
        onDragOver={(e) => e.preventDefault()}
        onDrop={(e) => {
          e.preventDefault();
          handleFile(e.dataTransfer.files?.[0]);
        }}
        style={{
          position: 'relative',
          width: 'min(720px, 100%)',
          aspectRatio: '16 / 9',
          // Black only once an image is in (it doubles as the letterbox
          // preview); empty it's a plain drop target on the modal's surface.
          background: prepared ? '#000' : 'transparent',
          border: prepared ? '1px solid var(--line, #444)' : '2px dashed var(--line, #888)',
          borderRadius: 6,
          overflow: 'hidden',
          flex: 'none',
        }}
      >
        {prepared ? (
          <img
            src={prepared.previewUrl}
            alt=""
            style={{
              position: 'absolute',
              inset: 0,
              width: '100%',
              height: '100%',
              objectFit: prepared.aspectOk ? 'cover' : 'contain',
            }}
          />
        ) : (
          <button
            type="button"
            onClick={() => fileRef.current?.click()}
            disabled={processing || busy}
            style={{
              position: 'absolute',
              inset: 0,
              width: '100%',
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '0.6rem',
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              color: 'inherit',
              font: 'inherit',
            }}
          >
            <span aria-hidden="true" style={{ fontSize: 'var(--text-display-lg)', lineHeight: 1, opacity: 0.55 }}>
              ⬆
            </span>
            <span style={{ fontSize: 'var(--text-body-md)', fontWeight: 600 }}>
              {processing ? 'Preparing…' : 'Drop a PNG / JPG export here'}
            </span>
            {!processing && (
              <span className="muted small">or click to choose a file: it previews here before anything is added</span>
            )}
          </button>
        )}
        {ghost && (
          <>
            <span
              style={{
                position: 'absolute',
                top: '4%',
                left: '3.3%',
                fontSize: 'var(--text-label-md)',
                letterSpacing: '.18em',
                textTransform: 'uppercase',
                color: '#fff',
                mixBlendMode: 'difference',
                pointerEvents: 'none',
              }}
            >
              RKH
            </span>
            <span
              style={{
                position: 'absolute',
                top: '4%',
                right: '3.6%',
                fontSize: 'var(--text-label-sm)',
                letterSpacing: '.18em',
                textTransform: 'uppercase',
                color: '#fff',
                mixBlendMode: 'difference',
                pointerEvents: 'none',
              }}
            >
              Section
            </span>
            <span
              style={{
                position: 'absolute',
                bottom: '5%',
                right: '3.6%',
                fontSize: 'var(--text-label-sm)',
                letterSpacing: '.18em',
                textTransform: 'uppercase',
                color: '#fff',
                mixBlendMode: 'difference',
                pointerEvents: 'none',
              }}
            >
              01
            </span>
          </>
        )}
      </div>

      {error && (
        <p className="small" style={{ margin: 0, color: 'var(--danger, #e5534b)' }}>
          {error}
        </p>
      )}

      {prepared && !prepared.aspectOk && (
        <p className="muted small" style={{ margin: 0 }}>
          This image is {prepared.width}×{prepared.height}, not 16:9, so it will be letterboxed on black as shown.
          Re-export at 3840×2160 to fill the slide.
        </p>
      )}
      {prepared &&
        prepared.warnings.map((w) => (
          <p key={w} className="muted small" style={{ margin: 0 }}>
            {w}
          </p>
        ))}

      {prepared && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Slide title (for the filmstrip)"
            maxLength={120}
            style={{ flex: '1 1 220px', minWidth: 180 }}
          />
          <label
            className="small"
            style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', whiteSpace: 'nowrap' }}
          >
            <input type="checkbox" checked={chrome} onChange={(e) => setChrome(e.target.checked)} />
            RKH chrome (logo, section, page number)
          </label>
        </div>
      )}

      <div style={{ display: 'flex', gap: '0.6rem' }}>
        {prepared && (
          <>
            <button type="button" className="btn btn--primary" onClick={insert} disabled={busy || processing}>
              {busy ? 'Inserting…' : prepared.aspectOk ? 'Insert slide' : 'Insert letterboxed on black'}
            </button>
            <button
              type="button"
              className="btn btn--ghost"
              onClick={() => fileRef.current?.click()}
              disabled={busy || processing}
            >
              Choose a different image
            </button>
          </>
        )}
      </div>

      <input
        ref={fileRef}
        type="file"
        accept="image/png,image/jpeg,image/webp"
        style={{ display: 'none' }}
        onChange={(e) => {
          handleFile(e.target.files?.[0]);
          e.target.value = '';
        }}
      />
    </div>
  );
}
